import styles from "./Poster.module.css";
import { Map } from "PosterEditor/map/map";
import { useState, useRef, useEffect, useCallback } from "react";
import { useMapboxProxy } from "PosterEditor/utils/mapbox-proxy";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import { MapElement } from "PosterEditor/utils/canvas-drawing";
import { MapStaticImageOptions } from "PosterEditor/model/poster-project";
import { useImageSnapshotStore } from "entity-store/image-store/snapshot/use-image-snapshot";
import {
  PosterSnapshotRendererProps,
  renderSnapshotToImage,
} from "./snapshot-helper";
import { imageUrlToBase64 } from "./snapshot-helper";
import { ProjectRenderConfig } from "PosterEditor/utils/use-render-project";
import { useRenderProject } from "PosterEditor/utils/use-render-project";
import { EditorContextType } from "PosterEditor/PosterEditor";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import { usePaths } from "BEDROCK/paths";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";
import { useCheckout } from "BEDROCK/pages/checkout/checkout-provider";
import { ActivityIndicator } from "BEDROCK/shared-components/activity-indicator";
import { useProjectStore } from "entity-store/project-store/use-project-store";

interface PosterProps {
  onShowContextualEditor: (show: boolean) => void;
  contextType?: EditorContextType;
  isMobile?: boolean;
}

export const Poster = (props: PosterProps) => {
  const {
    project,
    template,
    setTemplateThumbnail,
    staticMapImageURL,
    gpsSource,
  } = usePosterProject();
  const [isStaticImageLoaded, setIsStaticImageLoaded] =
    useState<boolean>(false);
  const { addProject: addToCheckout } = useCheckout();
  const { deleteDraft } = useProjectStore();
  const { checkoutReview, navigateTo } = usePaths();
  const { setSnapshotFunction } = useEditorUI();
  const { storeSnapshot } = useImageSnapshotStore();
  const posterContainerRef = useRef<any>(null);
  const mapContainerRef = useRef<any>(null);
  const {
    isLoading: sizePresetsLoading,
    screenSizeFromProject,
    cssAspectRatio,
    fullRenderSizeFromProject,
    borderWidthInPixels,
    fullThumbnailSizeFromProject,
    mapThumbnailSizeFromProject,
    mapRenderSizeFromProject,
  } = useSizePresets();

  const [mapCanvas, setMapCanvas] = useState<any>(null);

  const [mapRef, setMapRef] = useState<any>(null);
  const { fetchMapboxImageViaProxy } = useMapboxProxy();
  const [elementUnderCursor, setElementUnderCursor] = useState<
    MapElement | undefined
  >(undefined);

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const [editingLabel, setEditingLabel] = useState<number | null>(null);

  const { session } = useAuthContext();

  const { renderProject, isRendering } = useRenderProject(
    session?.access_token
  );
  const [renderError, setRenderError] = useState<boolean>(false);

  const updateDimensions = () => {
    if (mapContainerRef.current) {
      const { offsetWidth, offsetHeight } = mapContainerRef.current;
      setDimensions({
        width: offsetWidth,
        height: offsetHeight,
      });
    }
  };

  const handleRenderSuccess = async () => {
    deleteDraft();
    await addToCheckout(project.id);
    navigateTo(checkoutReview);
  };

  const performRender = useCallback(() => {
    if (session && !isRendering) {
      if (props.contextType === "rendering") {
        if (mapRef?.current) {
          const config: ProjectRenderConfig = {
            map: mapRef,
            project: project,
            gpsSource: gpsSource,
          };
          renderProject(config)
            .then(async () => {
              await handleRenderSuccess();
            })
            .catch((err) => {
              console.error("Error rendering project:", err);
              setRenderError(true);
            });
        }
      }
    }
  }, [
    session,
    isRendering,

    props.contextType,
    gpsSource,
    project,
    mapRef,
    renderProject,
  ]);
  // const performMapRender = useCallback(() => {

  useEffect(() => {
    performRender();
  }, [mapRef, session, props.contextType]);

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  useEffect(() => {
    updateDimensions();
  }, [project.size]);

  useEffect(() => {}, [mapRef]);

  const takeThumbnailSnapshot = () => {
    setIsStaticImageLoaded(false);
    updateStaticMapImageURL(true);
  };

  const performSnapshot = (image: string, isThumbnail: boolean) => {
    if (!mapCanvas) {
      return;
    }
    if (template) {
      const canvasEl = mapCanvas as HTMLCanvasElement;
      canvasEl.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);

          const ffPosterSize = isThumbnail
            ? fullThumbnailSizeFromProject(project)
            : fullRenderSizeFromProject(project, false);

          const ffMapSize = isThumbnail
            ? mapThumbnailSizeFromProject(project)
            : mapRenderSizeFromProject(project, false);

          const screenBorderWidth = borderWidthInPixels(project) ?? 0;

          const screenSize = screenSizeFromProject(project) ?? {
            width: 0,
            height: 0,
          };
          const screenMapWidth = screenSize.width - screenBorderWidth * 2;
          const scale = ffMapSize.width / screenMapWidth;

          const diffX = ffPosterSize.width - ffMapSize.width;

          const borderMargin = diffX / 2;
          const renderProps: PosterSnapshotRendererProps = {
            mapImageUrl: image,
            overlayImage: url,
            posterSize: {
              width: ffPosterSize.width,
              height: ffPosterSize.height,
            },
            mapSize: {
              width: ffMapSize.width,
              height: ffMapSize.height,
            },
            borderColor: project.borderStyle.borderColor ?? "white",
            borderMargin: borderMargin,
            scale: scale,
          };
          renderSnapshotToImage(renderProps)
            .then((dataUrl) => {
              if (isThumbnail) {
                storeSnapshot(dataUrl, template.thumbnail, true).then(
                  (imageId) => {
                    if (imageId) {
                      setTemplateThumbnail(imageId);
                    }
                  }
                );
              }
              // Save or use the image
            })
            .catch((err) => {
              console.error("Failed to render snapshot:", err);
            });
        } else {
          console.error("Failed to convert canvas to blob");
        }
      }, "image/png");
    }
  };

  const updateStaticMapImageURL = (thumbnail: boolean) => {
    if (!mapRef) {
      return;
    }
    const m = mapRef.current as mapboxgl.Map;
    const bounds = m.getBounds();
    if (!bounds) {
      return;
    }
    const northeast = bounds?.getNorthEast();
    const southwest = bounds?.getSouthWest();

    const options: MapStaticImageOptions = {
      northeast,
      southwest,
      isThumbnail: thumbnail,
      project,
    };
    const url = staticMapImageURL(options);
    if (url) {
      if (thumbnail) {
        imageUrlToBase64(url).then((image) => {
          performSnapshot(image, thumbnail);
        });
      }
    }
  };

  useEffect(() => {
    if (mapRef) {
      setSnapshotFunction(takeThumbnailSnapshot);
    }
  }, [mapRef, project.mapStyle]);

  if (sizePresetsLoading) {
    return <div>Loading size presets...</div>;
  }

  const aspectRatio = cssAspectRatio(project);
  const screenSize = screenSizeFromProject(project) ?? { width: 0, height: 0 };

  return (
    <div
      style={{
        width: "100%",
        height: screenSize.height,
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        aspectRatio: aspectRatio,
      }}
    >
      <div
        ref={posterContainerRef}
        id="posterContainerRef"
        className={styles.poster}
        style={{
          width: screenSize.width,
          height: screenSize.height,
        }}
      >
        <div
          ref={mapContainerRef}
          style={{
            width: screenSize.width,
            height: screenSize.height,
          }}
        >
          <div
            style={{
              width: screenSize.width,
              height: screenSize.height,
              backgroundColor: project.borderStyle.borderColor,
              padding: borderWidthInPixels(project),
              boxSizing: "border-box",
            }}
          >
            <Map
              onClick={() => {
                setEditingLabel(null);
              }}
              onMove={() => {
                setEditingLabel(null);
              }}
              onMapLoad={setMapRef}
              onStaticImageURLFetched={() => {}}
              onChangeElementUnderCursor={setElementUnderCursor}
              onShowElementEditor={props.onShowContextualEditor}
              onLoadCanvas={setMapCanvas}
              isMobile={props.isMobile}
            />
          </div>
        </div>
        {props.contextType === "rendering" && (
          <div
            className={styles.renderingOverlay}
            style={{
              width: screenSize.width,
              height: screenSize.height,
            }}
          >
            <div className={styles.background} />
            <div className={styles.textContainer}>
              {renderError ? (
                <>
                  <span className={styles.error}>Rendering error</span>
                  <CTAButton title="Try again" onClick={performRender} />
                </>
              ) : (
                <>
                  <span>Composing your high resolution image</span>
                  <ActivityIndicator />
                </>
              )}
            </div>
          </div>
        )}
        {/* <button
          onClick={() => {
            performMapRender();
          }}
        >
          Get static map url
        </button> */}
      </div>
    </div>
  );
};
